import classNames from 'classnames';
import React, {ComponentType} from 'react';

interface IProps {
  className?: string;
  category?: 'primary' | 'secondary' | 'secondarycolor' | 'danger' | 'success';
  size?: 'small' | 'medium';
  disabled?: boolean;
  Icon?: ComponentType<{className?: string}>;
  label: string;
  onClick?: () => void;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

const SIZE_SMALL = 'px-2 py-1.5 text-sm';
const SIZE_DEFAULT = 'px-3 py-2 text-sm font-semibold';

const DEFAULT_LAYOUT = 'block flex rounded-md text-center';

const ICON_LAYOUT =
  'flex p-2 flex-shrink-0 items-center justify-center rounded-full';

const PRIMARY =
  'bg-blue-600 text-white shadow-sm hover:text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';

const SECONDARY = 'leading-6 text-gray-900';
const SECONDARY_COLOR = 'leading-6 text-blue-600';

const DANGER =
  'bg-red-600 text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600';
const DISABLED =
  'bg-gray-100 text-gray-400 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200 select-none';
const SUCCESS =
  'bg-green-600 text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600';

const getDefaultClassName = (
  category: IProps['category'],
  size: IProps['size'],
  disabled: boolean,
) => {
  return getClassName(
    category,
    classNames(DEFAULT_LAYOUT, size === 'small' ? SIZE_SMALL : SIZE_DEFAULT),
    disabled,
  );
};

const getIconClassName = (category: IProps['category'], disabled: boolean) => {
  return getClassName(category, ICON_LAYOUT, disabled);
};

const getClassName = (
  category: IProps['category'],
  layout: string,
  disabled: boolean,
) => {
  if (disabled) {
    return classNames(layout, DISABLED);
  }
  switch (category) {
    case 'primary':
      return classNames(layout, PRIMARY);
    case 'secondary':
      return classNames(layout, SECONDARY);
    case 'secondarycolor':
      return classNames(layout, SECONDARY_COLOR);
    case 'danger':
      return classNames(layout, DANGER);
    case 'success':
      return classNames(layout, SUCCESS);
    default:
      return classNames(layout, PRIMARY);
  }
};

export const Button: React.FC<IProps> = (props) => {
  return (
    <button
      {...props.buttonProps}
      disabled={props.disabled}
      className={classNames(
        getDefaultClassName(
          props.category,
          props.size,
          props.disabled === true,
        ),
        props.className,
      )}
      onClick={props.onClick}>
      {props.Icon && <props.Icon className="-ml-0.5 mr-1.5 h-5 w-5" />}
      {props.label}
    </button>
  );
};

interface ILinkProps extends Omit<IProps, 'buttonProps' | 'onClick'> {
  href: string;
  linkProps?: React.AnchorHTMLAttributes<HTMLAnchorElement>;
}

export const LinkButton: React.FC<ILinkProps> = (props) => {
  return props.disabled === true ? (
    <span
      className={classNames(
        getDefaultClassName(props.category, props.size, true),
        props.className,
      )}>
      {props.Icon && <props.Icon className="-ml-0.5 mr-1.5 h-5 w-5" />}
      {props.label}
    </span>
  ) : (
    <a
      {...props.linkProps}
      href={props.href}
      className={getDefaultClassName(props.category, props.size, false)}>
      {props.Icon && <props.Icon className="-ml-0.5 mr-1.5 h-5 w-5" />}
      {props.label}
    </a>
  );
};

interface IIconButtonProps extends Omit<IProps, 'label' | 'size'> {
  children: React.ReactNode;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
}

export const IconButton: React.FC<IIconButtonProps> = (props) => {
  const {size = 'large'} = props;
  return (
    <button
      {...props.buttonProps}
      disabled={props.disabled}
      className={classNames(
        getIconClassName(props.category, props.disabled === true),
        size === 'xsmall'
          ? classNames(
              'p-0 bg-transparent',
              props.disabled === true
                ? 'opacity-60 hover:bg-transparent'
                : 'hover:!bg-blue-100',
            )
          : size === 'small'
            ? 'p-1'
            : size === 'medium'
              ? 'p-1.5'
              : size === 'xlarge'
                ? 'p-3'
                : 'p-2',
        props.className,
      )}
      onClick={props.onClick}>
      <div className="h-5 w-5">{props.children}</div>
    </button>
  );
};

interface IFilterButtonProps extends Omit<IProps, 'size'> {
  selected?: boolean;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
}

const getFilterSizeClass = (size: IFilterButtonProps['size']) => {
  switch (size) {
    case 'xsmall':
      return 'px-2.5 py-1 text-xs';
    case 'small':
      return 'px-2.5 py-1 text-sm';
    case 'medium':
      return 'px-3 py-1.5 text-sm';
    case 'large':
      return 'px-3 py-2 text-sm';
    case 'xlarge':
      return 'px-4 py-2.5 text-sm';
    default:
      return 'px-3 py-1.5 text-sm';
  }
};

const getFilterColorClass = (selected: boolean) => {
  return selected
    ? 'bg-blue-600 text-white ring-blue-600 hover:bg-blue-500'
    : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50';
};
export const FilterButton: React.FC<IFilterButtonProps> = (props) => {
  return (
    <button
      {...props.buttonProps}
      disabled={props.disabled}
      className={classNames(
        'rounded-full font-semibold ring-1 ring-inset ',
        getFilterSizeClass(props.size),
        getFilterColorClass(props.selected === true),
      )}
      onClick={props.onClick}>
      {props.label}
    </button>
  );
};
